import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Activity, BarChart3, TrendingUp, FileText, ArrowRight, DollarSign, ArrowLeftRight, Trophy, ShieldAlert, MailCheck } from 'lucide-react';

interface ReportCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  onNavigate: () => void;
}

function ReportCard({ title, description, icon, onNavigate }: ReportCardProps) {
  return (
    <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={onNavigate}>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">{icon}</div>
          <div className="flex-1">
            <CardTitle className="text-lg">{title}</CardTitle>
            <CardDescription className="mt-1">{description}</CardDescription>
          </div>
          <ArrowRight className="h-5 w-5 text-muted-foreground" />
        </div>
      </CardHeader>
    </Card>
  );
}

interface ReportCardDef {
  section: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  /** Any of these permissions grants access. Must mirror SECTION_ACCESS. */
  permissions: string[];
}

const REPORT_CARDS: ReportCardDef[] = [
  {
    section: 'pipeline-performance-report',
    title: 'Pipeline Performance',
    description: 'Analyze historical Created, Application, Locked, and Funded production by period',
    icon: <Activity className="h-5 w-5 text-primary" />,
    permissions: ['view_reports', 'generate_reports']
  },
  {
    section: 'pipeline-status-report',
    title: 'Pipeline Status',
    description: 'View the current pipeline snapshot by folder, lock date, and milestone status',
    icon: <BarChart3 className="h-5 w-5 text-primary" />,
    permissions: ['view_reports', 'generate_reports']
  },
  {
    section: 'funded-loan-report',
    title: 'Funded Loans',
    description: 'Historical funded production by funded date, regardless of current status',
    icon: <DollarSign className="h-5 w-5 text-primary" />,
    permissions: ['view_reports', 'generate_reports']
  },
  {
    section: 'lo-scoreboard-report',
    title: 'LO Scoreboard',
    description: 'Loan officers ranked by funded volume, loan count, and loan mix for any period',
    icon: <Trophy className="h-5 w-5 text-primary" />,
    permissions: ['generate_reports', 'view_lo_scoreboard']
  },
  {
    section: 'pickup-report',
    title: 'Pick Up',
    description: 'Find better investor pricing for locked, unpurchased loans',
    icon: <TrendingUp className="h-5 w-5 text-primary" />,
    permissions: ['generate_reports']
  },
  {
    section: 'company-revenue-report',
    title: 'Company Revenue',
    description: 'Wire decomposition: true revenue, expenses, and pass-through for finalized loans',
    icon: <ArrowLeftRight className="h-5 w-5 text-primary" />,
    permissions: ['generate_reports']
  },
  {
    section: 'fraud-alerts-report',
    title: 'Fraud Alerts',
    description: 'Review amended-return detections, email outcomes, and dispositions',
    icon: <ShieldAlert className="h-5 w-5 text-primary" />,
    permissions: ['view_reports', 'generate_reports']
  },
  {
    section: 'appraisal-delivery-report',
    title: 'Appraisal Processes',
    description: 'Borrower deliveries, CDA ordering, appraisal fee discrepancies, and payment links',
    icon: <MailCheck className="h-5 w-5 text-primary" />,
    permissions: ['view_reports', 'generate_reports']
  }
];

interface CompanyReportsHubProps {
  onNavigate?: (section: string) => void;
  showLegacyPickup: boolean;
  /**
   * Permission check (from useRoleAccess). Cards the user can't open are hidden.
   * The route-level gate (SECTION_ACCESS) is the actual enforcement; this just
   * avoids surfacing reports the user cannot access. Required (no allow-all
   * default) so callers can't accidentally render an ungated hub.
   */
  hasAnyPermission: (permissions: string[]) => boolean;
}

export function CompanyReportsHub({
  onNavigate,
  showLegacyPickup,
  hasAnyPermission
}: CompanyReportsHubProps) {
  const handleNavigate = (section: string) => {
    if (onNavigate) {
      onNavigate(section);
    }
  };

  const visibleCards = REPORT_CARDS.filter((card) => (
    (card.section !== 'pickup-report' || showLegacyPickup)
    && hasAnyPermission(card.permissions)
  ));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <FileText className="h-6 w-6 text-primary" />
          Company Reports
        </h1>
        <p className="text-muted-foreground">
          Access reports and analytics for your organization
        </p>
      </div>

      {/* Report Cards */}
      {visibleCards.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {visibleCards.map((card) => (
            <ReportCard
              key={card.section}
              title={card.title}
              description={card.description}
              icon={card.icon}
              onNavigate={() => handleNavigate(card.section)}
            />
          ))}
        </div>
      )}

      {/* Coming Soon Section */}
      <Card className="border-dashed">
        <CardContent className="py-8 text-center">
          <p className="text-muted-foreground">
            More reports coming soon. Contact your administrator for custom report requests.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

export default CompanyReportsHub;
